import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Divider,
    Flex,
    Grid,
    GridItem,
    Stack,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { agentsService } from "services/agentsService";
import { formatDocument } from "utils/utils";
import { LOCALE_PT_BR } from "constants/date-time";
import { ROUTES } from "constants/routes-path";
import AgentTimeline from "./AgentTimeline";

function AgentDetails() {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();

    const textColor = useColorModeValue("gray.700", "white");
    const primaryOnBg = useColorModeValue("gray.900", "gray.400");
    const cardBg = useColorModeValue("gray.50", "gray.700");

    const [agentData, setAgentData] = useState([]);
    const [personData, setPersonData] = useState([]);
    const [agentHistoryData, setAgentHistoryData] = useState([]);

    useEffect(() => {
        if (id) {
            fetchAgent(id);
        }
    }, [id]);

    const fetchAgent = async (id) => {
        try {
            const agent = await agentsService.find(id);
            const agentHistory = await agentsService.findHistory(id);
            setAgentData(agent);
            setPersonData(agent.id_pessoa);
            setAgentHistoryData(Array.isArray(agentHistory) ? agentHistory.sort((a, b) => new Date(a.data) - new Date(b.data)) : []);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    return (
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
            <Card px="0px" mb="24px">
                <CardHeader px="22px" mb="24px">
                    <Flex direction="column">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t("settings.agent.details.title")}
                        </Text>
                    </Flex>
                </CardHeader>
                <CardBody px="22px">
                    <Tabs variant="enclosed">
                        <TabList overflowX="auto" whiteSpace="nowrap" sx={{ "&::-webkit-scrollbar": { display: "none" }}}>
                            <Tab>{t("settings.agent.details.tab-general.title")}</Tab>
                            <Tab>{t("settings.agent.details.tab-person.title")}</Tab>
                            <Tab>{t("settings.agent.details.tab-timeline.title")}</Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel>
                                <Card border="1px solid #3884cc" bg={cardBg} >
                                    <CardHeader>
                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                            {t("settings.agent.details.tab-general.subtitle")}
                                        </Text>
                                    </CardHeader>
                                    <Divider borderColor="#3884cc" mb="20px" />
                                    <CardBody>
                                        <Stack spacing={4}>
                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-general.registration")}:
                                                    </Text>{"\u00A0"}
                                                    {agentData.codigo ?? "-"}
                                                </GridItem>
                                            </Grid>
                                        </Stack>
                                    </CardBody>
                                </Card>
                            </TabPanel>

                            <TabPanel>
                                <Card border="1px solid #3884cc" bg={cardBg}>
                                    <CardHeader>
                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                            {t("settings.agent.details.tab-person.title")}
                                        </Text>
                                    </CardHeader>
                                    <Divider borderColor="#3884cc" mb="20px" />
                                    <CardBody>
                                        <Stack spacing={4}>
                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                                                <GridItem fontSize="md" minW={0}>
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.cpfcnpj")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.cpf_cnpj ? formatDocument(personData.cpf_cnpj) : "-"}
                                                    <Text as="span" fontWeight="bold" ml={4}>
                                                        {t("settings.agent.details.tab-person.surname")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.apelido ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.name")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.nome ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.social-name")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.nome_social ?? "-"}
                                                </GridItem>
                                            </Grid>

                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.rg")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.numero_identidade ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.uf-rg")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.id_uf_identidade ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.issuing-agency")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.orgao_expedidor_identidade ?? "-"}
                                                </GridItem>
                                            </Grid>

                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.phone")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.telefone ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.cellphone")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.celular ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.email")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.email ?? "-"}
                                                </GridItem>
                                            </Grid>

                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.address-street")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.endereco_logradouro ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.address-number")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.endereco_numero ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.address-district")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.endereco_bairro ?? "-"}
                                                </GridItem>
                                            </Grid>

                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.address-city")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.endereco_municipio ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.address-state")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.endereco_uf ?? "-"}
                                                </GridItem>

                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.agent.details.tab-person.address-zip-code")}:
                                                    </Text>{"\u00A0"}
                                                    {personData.endereco_cep ?? "-"}
                                                </GridItem>
                                            </Grid>
                                        </Stack>
                                    </CardBody>
                                </Card>
                            </TabPanel>
                            
                            <TabPanel>
                                <Card border="1px solid #3884cc" bg={cardBg} >
                                    <AgentTimeline
                                        creationDate={new Date(agentData?.dataCriacao).toLocaleString(LOCALE_PT_BR)}
                                        historyData={agentHistoryData}
                                    />
                                </Card>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                    <Flex justifyContent="flex-start">
                        <Button
                            variant="light"
                            fontSize="sm"
                            alignSelf="flex-end"
                            mt="24px"
                            w="125px"
                            onClick={() => navigate(ROUTES.agentsPath)}
                        >
                            {t("system.actions.back")}
                        </Button>
                    </Flex>
                </CardBody>
            </Card>
        </Flex>
    );
}

export default AgentDetails;